import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { GET_API_CALL, PUT_API_CALL } from "../../../utils/services";
import { ENDPOINTS } from "../../../utils/endPoints";
import { useNavigate, useParams } from "react-router-dom";
import CustomPagination from "../../../components/customPagination";
import { BsSortAlphaDown, BsSortAlphaDownAlt } from "react-icons/bs";
import { FaStar } from "react-icons/fa";
import { FaRegStar } from "react-icons/fa";
import {
  CTable,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
  CTableBody,
  CTableDataCell,
  CFormSelect,
  CButton,
} from "@coreui/react";
import ProperSpinner from "../../../components/spinner/ProperSpinner";
import {
  createParams,
  getDateAndTimeFormatted,
  resolvePublicAssetUrl,
} from "../../../utils/commonFunctions";
import CIcon from "@coreui/icons-react";
import { cilPeople } from "@coreui/icons";
import { FiEdit, FiFilter } from "react-icons/fi";
import ModalV2 from "../../../components/customModal/ModalV2";
import toast from "react-hot-toast";
import ROUTE_NAMES from "../../../routes/routeNames";
import {
  REVIEW_RATING_OPTIONS,
  REVIEW_STATUS_OPTIONS,
} from "../../../utils/contentTypes";
import { UPDATE_RATINGS_REVIEWS_FILTERS } from "../../../store/actionTypes";
import { getRatingsFiltersForScope } from "../../ratingsAndReviews/reducer";
import DefaultProfilePic from "../../../assets/images/DefaultProfilePic.jpg";

const DEFAULT_PROFILE_PIC = resolvePublicAssetUrl(DefaultProfilePic);

const ReviewUserAvatar = ({ profileImage, status }) => {
  const [imgSrc, setImgSrc] = useState(
    () => profileImage || DEFAULT_PROFILE_PIC,
  );

  useEffect(() => {
    setImgSrc(profileImage || DEFAULT_PROFILE_PIC);
  }, [profileImage]);

  return (
    <div className="avatar avatar-sm">
      <img
        src={imgSrc}
        className="avatar-img"
        style={{ objectFit: "cover" }}
        alt=""
        onError={() => {
          if (imgSrc !== DEFAULT_PROFILE_PIC) {
            setImgSrc(DEFAULT_PROFILE_PIC);
          }
        }}
      />
      <span
        className={`avatar-status bg-${status === 1 ? "success" : "warning"}`}
      />
    </div>
  );
};

const RatingsAndReviews = ({
  withContainer = true,
  showSiteColumn = false,
}) => {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const filterScopeKey = siteId || "global";
  const filters = useSelector((state) =>
    getRatingsFiltersForScope(state, filterScopeKey),
  );

  const [reviews, setReviews] = useState([]);
  const [modal, setModal] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [statusChanged, setStatusChanged] = useState(false);
  const [filterModal, setFilterModal] = useState(false);
  const [localFilters, setLocalFilters] = useState({
    rating: filters.rating,
    status: filters.status,
  });

  const [tableState, setTableState] = useState({
    pageNo: 1,
    limit: 10,
    total: 0,
    totalPage: 1,
    nextHit: false,
    searchKey: "",
    sortBy: "createdAt",
    sortOrder: -1,
  });

  const [loading, setLoading] = useState(false);

  const updateAppliedFilters = (nextFilters) => {
    dispatch({
      type: UPDATE_RATINGS_REVIEWS_FILTERS,
      payload: {
        scopeKey: filterScopeKey,
        filters: nextFilters,
      },
    });
  };

  const updateReviewStatusHandler = () => {
    const toastId = toast.loading("Updating review status...");
    PUT_API_CALL(
      ENDPOINTS.RATINGS_AND_REVIEWS.UPDATE_STATUS(selectedReview._id),
      { status: selectedStatus },
      (res) => {
        console.log("update review status response :", res);
        if (res.isSuccess) {
          toast.success(res.data.message, {
            id: toastId,
          });
          setStatusChanged((prev) => !prev);
          setModal(false);
          getRatingsAndReviews();
        } else {
          toast.error(res.message || "Failed to update review status.", {
            id: toastId,
          });
        }
      },
    );
  };

  const getRatingsAndReviews = () => {
    setLoading(true);

    const query = createParams({
      siteId,
      rating: filters.rating,
      status: filters.status,
      pageNo: tableState.pageNo,
      limit: tableState.limit,
      searchKey: tableState.searchKey,
      sortBy: tableState.sortBy,
      sortOrder: tableState.sortOrder,
    });

    GET_API_CALL(ENDPOINTS.RATINGS_AND_REVIEWS.LIST, query, (res) => {
      setReviews(res.data.data || []);

      setTableState((prev) => ({
        ...prev,
        pageNo: res.data.pageNo,
        limit: res.data.limit,
        total: res.data.total,
        totalPage: res.data.totalPage,
        nextHit: res.data.nextHit,
      }));

      setLoading(false);
    });
  };

  const updatePage = (page) => {
    setTableState((prev) => ({
      ...prev,
      pageNo: page,
    }));
  };

  const pageLimitHandler = (e) => {
    setTableState((prev) => ({
      ...prev,
      limit: Number(e.target.value),
      pageNo: 1,
    }));
  };

  const [sortApplied, setSortApplied] = useState("createdAt");

  const handlerSort = (sortBy) => {
    setSortApplied(sortBy);

    setTableState((prev) => ({
      ...prev,
      sortBy,
      sortOrder: prev.sortOrder === 1 ? -1 : 1,
    }));
  };

  const handleApplyFilter = () => {
    const nextFilters = {
      rating: localFilters.rating,
      status: localFilters.status,
    };

    updateAppliedFilters(nextFilters);
    setFilterModal(false);

    setTableState((prev) => ({
      ...prev,
      pageNo: 1,
    }));
  };

  const handleResetFilter = () => {
    const resetFilters = { rating: "", status: "" };
    setLocalFilters(resetFilters);
    updateAppliedFilters(resetFilters);
    setFilterModal(false);
    setTableState((prev) => ({ ...prev, pageNo: 1 }));
  };

  const openFilterModal = () => {
    setLocalFilters({
      rating: filters.rating,
      status: filters.status,
    });
    setFilterModal(true);
  };

  const isFilterActive = Boolean(filters.rating || filters.status);

  useEffect(() => {
    getRatingsAndReviews();
  }, [
    siteId,
    tableState.pageNo,
    tableState.limit,
    tableState.searchKey,
    tableState.sortBy,
    tableState.sortOrder,
    filters.rating,
    filters.status,
  ]);

  const renderNoDataFound = () => {
    if (loading) {
      return (
        <div className="ndf-container">
          <h3 className="ndf-heading">
            <span>{" Fetching Ratings and Reviews "}</span> {"..."}
          </h3>
        </div>
      );
    }
    return (
      <div className="ndf-container">
        <h3 className="ndf-heading">
          {"No"}
          <span>{" Ratings and Reviews "}</span> {"available !!!"}
        </h3>
      </div>
    );
  };

  const containerStyle = withContainer
    ? {
        marginTop: "20px",
        padding: "20px",
        border: "1px solid #e0e0e0",
        borderRadius: "5px",
      }
    : null;

  return (
    <div style={containerStyle}>
      <div className="table-header-container">
        <h3 className="w-50 mb-0 text-primary">{"Ratings And Reviews List"}</h3>
        <div
          className="btn btn-outline-primary btn-sm add-btn add-btn-container"
          onClick={openFilterModal}
        >
          <span className="px-2">
            {"Filter"}
            {isFilterActive && <span className="filter-applied-dot" />}
          </span>
          <span className="add-icon">
            <FiFilter />
          </span>
        </div>
      </div>
      <CTable hover responsive>
        <CTableHead className="text-nowrap border">
          <CTableRow>
            <CTableHeaderCell className="bg-body-tertiary text-center table-heading-5-cell">
              S. No.
            </CTableHeaderCell>
            <CTableHeaderCell className="bg-body-tertiary table-heading-5-cell">
              <CIcon icon={cilPeople} />
            </CTableHeaderCell>

            <CTableHeaderCell
              className={`bg-body-tertiary table-heading-15-cell ${sortApplied === "firstName" ? "sort-highlight" : ""}`}
            >
              {"Full Name"}
              <span onClick={() => handlerSort("firstName")}>
                {tableState.sortOrder === 1 ? (
                  <BsSortAlphaDown className="sort-icons cursor-pointer" />
                ) : (
                  <BsSortAlphaDownAlt className="sort-icons cursor-pointer" />
                )}
              </span>
            </CTableHeaderCell>

            {showSiteColumn && (
              <CTableHeaderCell className={`bg-body-tertiary table-heading-15-cell ${sortApplied === "siteName" ? "sort-highlight" : ""}`}>
                Site Name
                <span onClick={() => handlerSort("siteName")}>
                {tableState.sortOrder === 1 ? (
                  <BsSortAlphaDown className="sort-icons cursor-pointer" />
                ) : (
                  <BsSortAlphaDownAlt className="sort-icons cursor-pointer" />
                )}
              </span>
              </CTableHeaderCell>
            )}

            <CTableHeaderCell
              className={`bg-body-tertiary table-heading-10-cell ${
                sortApplied === "rating" ? "sort-highlight" : ""
              }`}
            >
              Rating
              <span onClick={() => handlerSort("rating")}>
                {tableState.sortOrder === 1 ? (
                  <BsSortAlphaDown className="sort-icons cursor-pointer" />
                ) : (
                  <BsSortAlphaDownAlt className="sort-icons cursor-pointer" />
                )}
              </span>
            </CTableHeaderCell>

            <CTableHeaderCell
              className={`bg-body-tertiary table-heading-15-cell ${
                sortApplied === "createdAt" ? "sort-highlight" : ""
              }`}
            >
              Created At
              <span onClick={() => handlerSort("createdAt")}>
                {tableState.sortOrder === 1 ? (
                  <BsSortAlphaDown className="sort-icons cursor-pointer" />
                ) : (
                  <BsSortAlphaDownAlt className="sort-icons cursor-pointer" />
                )}
              </span>
            </CTableHeaderCell>

            <CTableHeaderCell
              className={`bg-body-tertiary table-heading-10-cell ${
                sortApplied === "status" ? "sort-highlight" : ""
              }`}
            >
              Status
              <span onClick={() => handlerSort("status")}>
                {tableState.sortOrder === 1 ? (
                  <BsSortAlphaDown className="sort-icons cursor-pointer" />
                ) : (
                  <BsSortAlphaDownAlt className="sort-icons cursor-pointer" />
                )}
              </span>
            </CTableHeaderCell>

            <CTableHeaderCell className="bg-body-tertiary table-heading-10-cell">
              {"Actions"}
            </CTableHeaderCell>
          </CTableRow>
        </CTableHead>
        {loading ? (
          <ProperSpinner />
        ) : (
          <CTableBody>
            {reviews.map((review, index) => (
              <CTableRow key={review._id}>
                <CTableDataCell className="text-center">
                  {(tableState.pageNo - 1) * tableState.limit + index + 1}
                </CTableDataCell>
                <CTableDataCell className="text-center">
                  <ReviewUserAvatar
                    profileImage={review.user?.profileImage}
                    status={review.user?.status}
                  />
                </CTableDataCell>
                <CTableDataCell
                  onClick={
                    review.user?.status !== 3
                      ? () => navigate(`/users/${review.user?._id}`)
                      : undefined
                  }
                  className={
                    review.user?.status !== 3
                      ? "name-link text-truncate"
                      : "text-truncate"
                  }
                  title={
                    review.user?.firstName + " " + review.user?.lastName ||
                    "N/A"
                  }
                >
                  {review.user?.firstName + " " + review.user?.lastName ||
                    "N/A"}
                </CTableDataCell>

                {showSiteColumn && (
                  <CTableDataCell
                    onClick={
                      review.site?._id
                        ? () => navigate(`/sites/${review.site?._id}`)
                        : undefined
                    }
                    className={
                      review.site?._id
                        ? "name-link text-truncate"
                        : "text-truncate"
                    }
                    title={review.site?.siteName || "N/A"}
                  >
                    {review.site?.siteName || "N/A"}
                  </CTableDataCell>
                )}

                <CTableDataCell>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      height: "100%",
                    }}
                  >
                    <span>{review.rating}</span>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "2px",
                      }}
                    >
                      {Array.from({ length: 5 }).map((_, i) =>
                        i < review.rating ? (
                          <FaStar key={i} color="gold" size={16} />
                        ) : (
                          <FaRegStar key={i} color="gold" size={16} />
                        ),
                      )}
                    </div>
                  </div>
                </CTableDataCell>

                <CTableDataCell>
                  {getDateAndTimeFormatted(review.createdAt)}
                </CTableDataCell>

                <CTableDataCell>{review.status}</CTableDataCell>

                <CTableDataCell
                  align="center"
                  className="cursor-pointer"
                  onClick={() => {
                    setModal((prev) => !prev);
                    setSelectedReview(review);
                    setSelectedStatus(review.status);
                  }}
                >
                  <FiEdit />
                </CTableDataCell>
              </CTableRow>
            ))}
          </CTableBody>
        )}
      </CTable>
      {reviews?.length ? (
        <CustomPagination
          limit={tableState.limit}
          pageNo={tableState.pageNo}
          totalPage={tableState.totalPage}
          limitHanlder={pageLimitHandler}
          pageChangeHanlder={updatePage}
        />
      ) : null}
      {(loading || reviews?.length == 0) && renderNoDataFound()}

      <ModalV2
        isOpen={filterModal}
        onClose={() => {
          setLocalFilters({
            rating: filters.rating,
            status: filters.status,
          });
          setFilterModal(false);
        }}
        title="Filter Reviews"
        size="small"
        cancelButtonLabel="Reset"
        confirmButtonLabel="Apply Filter"
        onConfirm={handleApplyFilter}
        onCancel={handleResetFilter}
        closeOnOverlayClick={false}
        cancelOnLeft
      >
        <div className="form-group-v2">
          <label htmlFor="reviewRating">Rating</label>
          <CFormSelect
            id="reviewRating"
            size="sm"
            value={localFilters.rating}
            onChange={(e) =>
              setLocalFilters((prev) => ({
                ...prev,
                rating: e.target.value,
              }))
            }
          >
            <option value="">Show All</option>
            {REVIEW_RATING_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </CFormSelect>
        </div>

        <div className="form-group-v2 mb-0">
          <label htmlFor="reviewStatus">Status</label>
          <CFormSelect
            id="reviewStatus"
            size="sm"
            value={localFilters.status}
            onChange={(e) =>
              setLocalFilters((prev) => ({
                ...prev,
                status: e.target.value,
              }))
            }
          >
            <option value="">Show All</option>
            {REVIEW_STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </CFormSelect>
        </div>
      </ModalV2>

      <ModalV2
        isOpen={modal}
        onClose={() => setModal(false)}
        title="Ratings and Reviews Details"
        size="large"
        confirmButtonLabel="Close"
        onConfirm={() => setModal(false)}
        hideFooter={true}
      >
        <div className="container-fluid">
          {/* User */}
          <div className="d-flex align-items-center mb-4">
            <div
              className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center fw-bold"
              style={{
                width: 60,
                height: 60,
                fontSize: 22,
              }}
            >
              {selectedReview?.user?.firstName?.charAt(0)}
            </div>

            <div className="ms-3">
              <h5 className="mb-1">
                {selectedReview?.user?.firstName}{" "}
                {selectedReview?.user?.lastName}
              </h5>
            </div>
          </div>

          <hr />

          {/* Rating */}

          <div className="row mb-4">
            <div className="col-md-6">
              <label className="fw-semibold mb-2">Rating</label>

              <div className="d-flex align-items-center gap-2">
                <span className="fw-bold fs-5">{selectedReview?.rating}</span>

                <div className="d-flex gap-1">
                  {Array.from({ length: 5 }).map((_, i) =>
                    i < selectedReview?.rating ? (
                      <FaStar key={i} color="#ffc107" />
                    ) : (
                      <FaRegStar key={i} color="#ffc107" />
                    ),
                  )}
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <label className="fw-semibold mb-2">Created At</label>

              <div>{getDateAndTimeFormatted(selectedReview?.createdAt)}</div>
            </div>
          </div>

          {/* Review */}

          <div className="mb-4">
            <label className="fw-semibold mb-2">Review</label>

            <div
              className="border rounded p-3 bg-light"
              style={{
                minHeight: 110,
                whiteSpace: "pre-wrap",
              }}
            >
              {selectedReview?.reviews || "No review provided."}
            </div>
          </div>

          {/* Status */}

          <div className="row align-items-end">
            <div className="col-md-6">
              <label className="fw-semibold mb-2">Status</label>

              <CFormSelect
                value={selectedStatus}
                onChange={(e) => {
                  setSelectedStatus(e.target.value);
                  setStatusChanged(e.target.value !== selectedReview?.status);
                }}
              >
                {REVIEW_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </CFormSelect>
            </div>

            <div className="col-md-6 text-end">
              <CButton
                color="primary"
                className="px-4"
                onClick={updateReviewStatusHandler}
                disabled={!statusChanged}
              >
                Save Changes
              </CButton>
            </div>
          </div>
        </div>
      </ModalV2>
    </div>
  );
};

export default RatingsAndReviews;
